import React, { useState, useEffect } from 'react';
import './styles/App.css';
import Header from './components/Header';
import ImageCarousel from './components/ImageCarousel';
import AboutSection from './components/AboutSection';
import PotteryCollection from './components/PotteryCollection';
import ContactSection from './components/ContactSection';
import SocialSection from './components/SocialSection';
import AdminPanel from './components/AdminPanel';
import PotteryModal from './components/PotteryModal';
import LanguageToggle from './components/LanguageToggle';
import AdminLoginModal from './components/AdminLoginModal';
import AnimatedBackground from './components/AnimatedBackground';
import { subscribeToPotteryItems, addPotteryItem, updatePotteryItem, deletePotteryItem, getPotteryItems } from './services/potteryService';

function App() {
    const [currentLang, setCurrentLang] = useState('ar');
    const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
    const [showAdminLogin, setShowAdminLogin] = useState(false);
    const [selectedPottery, setSelectedPottery] = useState(null);
    const [potteryItems, setPotteryItems] = useState([]);

    useEffect(() => {
        // Initialize language
        const savedLang = localStorage.getItem('language') || 'ar';
        setCurrentLang(savedLang);
        updateDocumentDirection(savedLang);

        // Check admin login
        const adminStatus = localStorage.getItem('adminLoggedIn') === 'true';
        setIsAdminLoggedIn(adminStatus);

        // Subscribe to pottery items
        const unsubscribe = subscribeToPotteryItems((items) => {
            setPotteryItems(items);
        });

        return () => {
            unsubscribe();
        };
    }, []);

    const updateDocumentDirection = (lang) => {
        document.documentElement.setAttribute('lang', lang);
        document.documentElement.setAttribute('dir', lang === 'ar' ? 'rtl' : 'ltr');
    };

    const toggleLanguage = () => {
        const newLang = currentLang === 'ar' ? 'en' : 'ar';
        setCurrentLang(newLang);
        localStorage.setItem('language', newLang);
        updateDocumentDirection(newLang);
    };

    const handleAdminLogin = (username, password) => {
        if (username === 'admin' && password === 'admin123') {
            setIsAdminLoggedIn(true);
            setShowAdminLogin(false);
            localStorage.setItem('adminLoggedIn', 'true');
            return true;
        }
        return false;
    };

    const handleLogout = () => {
        setIsAdminLoggedIn(false);
        localStorage.setItem('adminLoggedIn', 'false');
    };

    const handleAddPottery = async (newItem) => {
        try {
            // Ensure images
            if (!newItem.images || newItem.images.length === 0) {
                newItem.images = newItem.image ? [newItem.image] : ['pottery1.jpg'];
            }
            if (!newItem.image && newItem.images.length > 0) {
                newItem.image = newItem.images[0];
            }
            if (!newItem.imageType) {
                newItem.imageType = 'file';
            }
            if (!newItem.id) {
                newItem.id = Date.now().toString();
            } else {
                newItem.id = newItem.id.toString();
            }
            
            await addPotteryItem(newItem);
            const items = await getPotteryItems();
            setPotteryItems(items);
        } catch (error) {
            console.error('Error adding:', error);
            alert(currentLang === 'ar' 
                ? 'حدث خطأ أثناء الإضافة' 
                : 'Error adding item');
        }
    };

    const handleUpdatePottery = async (updatedItem) => {
        try {
            // Ensure images
            if (!updatedItem.images || updatedItem.images.length === 0) {
                updatedItem.images = updatedItem.image ? [updatedItem.image] : ['pottery1.jpg'];
            }
            if (!updatedItem.image && updatedItem.images.length > 0) {
                updatedItem.image = updatedItem.images[0];
            }
            if (!updatedItem.imageType) {
                updatedItem.imageType = 'file';
            }
            
            const itemId = updatedItem.id.toString();
            await updatePotteryItem(itemId, updatedItem);
            
            // Refresh immediately
            const items = await getPotteryItems();
            setPotteryItems(items);
        } catch (error) {
            console.error('Error updating:', error);
            alert(currentLang === 'ar' 
                ? 'حدث خطأ أثناء التحديث' 
                : 'Error updating item');
        }
    };

    const handleDeletePottery = async (id) => {
        try {
            await deletePotteryItem(id.toString());
            const items = await getPotteryItems();
            setPotteryItems(items);
        } catch (error) {
            console.error('Error deleting:', error);
            alert(currentLang === 'ar' 
                ? 'حدث خطأ أثناء الحذف' 
                : 'Error deleting item');
        }
    };

    return (
        <div className="App">
            <AnimatedBackground />
            <LanguageToggle currentLang={currentLang} onToggle={toggleLanguage} />
            
            <Header currentLang={currentLang} />
            
            <main className="main-content">
                <div className="container">
                    <ImageCarousel currentLang={currentLang} />
                    <AboutSection currentLang={currentLang} />
                    <PotteryCollection 
                        potteryItems={potteryItems}
                        currentLang={currentLang}
                        onItemClick={setSelectedPottery}
                    />
                    <ContactSection currentLang={currentLang} />
                    <SocialSection currentLang={currentLang} />
                </div>
            </main>

            {selectedPottery && (
                <PotteryModal
                    item={selectedPottery}
                    currentLang={currentLang}
                    onClose={() => setSelectedPottery(null)}
                />
            )}

            {showAdminLogin && (
                <AdminLoginModal
                    currentLang={currentLang}
                    onLogin={handleAdminLogin}
                    onClose={() => setShowAdminLogin(false)}
                />
            )}

            {isAdminLoggedIn && (
                <AdminPanel
                    currentLang={currentLang}
                    potteryItems={potteryItems}
                    onLogout={handleLogout}
                    onAdd={handleAddPottery}
                    onUpdate={handleUpdatePottery}
                    onDelete={handleDeletePottery}
                />
            )}

            {!isAdminLoggedIn && (
                <button 
                    className="admin-login-btn-hidden"
                    onClick={() => setShowAdminLogin(true)}
                    title={currentLang === 'ar' ? 'تسجيل دخول المدير' : 'Manager Login'}
                >
                    🔐
                </button>
            )}
        </div>
    );
}

export default App;

